import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FiX, FiCheckCircle, FiAlertCircle, FiEye, FiUploadCloud } from "react-icons/fi";
import { IoCameraOutline } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { uploadAuctionDocuments } from "../../features/documentsSlice";

const DocReviewModal = ({
  showUploadDocumentModal,
  setShowUploadDocumentModal,
  auctionItem,
  translate,
  documentLoading,
  onSuccess
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [auctionData, setAuctionData] = useState(auctionItem);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showPreview, setShowPreview] = useState("");
  const [selectedFile, setSelectedFile] = useState("");
  const [agree, setAgree] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setAuctionData(auctionItem);

    if (auctionItem?.required_documents?.length > 0) {
      const firstMissing = auctionItem.required_documents.find(
        (req) => !auctionItem.user_auction_documents?.some((u) => u.required_document?.id === req.id)
      );
      const initialDoc = firstMissing || auctionItem.required_documents[0];
      setSelectedDocument(initialDoc);

      const existingDoc = auctionItem.user_auction_documents?.find(
        (d) => d.required_document?.id === initialDoc.id
      );
      if (existingDoc) {
        setShowPreview(existingDoc.document);
      }
    }
  }, [auctionItem]);

  const isPdf = (url) =>
    url?.startsWith("data:application/pdf") ||
    url?.toLowerCase().endsWith(".pdf") ||
    url?.toLowerCase().includes("pdf?");

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError("Upload error! Make sure your file is valid and less than 5MB");
      return;
    }

    setError("");
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setShowPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleModalClose = () => {
    setSelectedDocument(null);
    setShowPreview("");
    setSelectedFile("");
    setAgree(false);
    setError("");
    setShowUploadDocumentModal(false);
  };

  const missingDocuments = auctionData?.required_documents?.filter(
    (reqDoc) => !auctionData?.user_auction_documents?.some((d) => d.required_document?.id === reqDoc.id)
  );

  const handleUploadSubmit = async () => {
    if (!selectedFile || !selectedDocument) {
      setError("Please select a valid file.");
      return;
    }

    const formData = new FormData();
    formData.append("name", selectedDocument.name);
    formData.append("required_document_id", selectedDocument.id);
    formData.append("auction", auctionData?.id);
    formData.append("document", selectedFile);

    try {
      setError("");

      const resultAction = await dispatch(uploadAuctionDocuments(formData)).unwrap();

      setAuctionData((prev) => {
        if (!prev) return prev;
        const updatedUserDocs = [...(prev.user_auction_documents || []), resultAction];
        return {
          ...prev,
          user_auction_documents: updatedUserDocs
        };
      });

      setSelectedFile("");
      setShowPreview(resultAction.document || "");
      setAgree(false);

      const remainingMissing = auctionData?.required_documents?.filter(
        (reqDoc) =>
          reqDoc.id !== selectedDocument.id &&
          !auctionData?.user_auction_documents?.some((d) => d.required_document?.id === reqDoc.id)
      );

      if (remainingMissing && remainingMissing.length > 0) {
        setSelectedDocument(remainingMissing[0]);
        setShowPreview("");
      }

      if (onSuccess) onSuccess(resultAction);
    } catch (err) {
      setError("Upload error! Make sure your file is valid and less than 5MB");
      console.error("Upload handler fault:", err);
    }
  };

  if (!showUploadDocumentModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="flex h-full max-h-[85vh] w-[94%] md:w-[75%] lg:w-[65%] flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        
        {/* Header Banner */}
        <div className="flex justify-between items-center w-full bg-gradient-to-r from-sky-700 via-lime-500 to-lime-50 border-b border-stone-400 py-3 px-4 md:px-8">
          <div className="text-white text-lg font-semibold">
            {translate(201)} <span className="text-xs text-sky-100/70 ml-2">(Lot: {auctionData?.lot_number})</span>
          </div>
          <div className="flex items-center space-x-4">
            {documentLoading && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            )}
            <FiX
              className="text-white text-2xl cursor-pointer hover:scale-105 hover:text-red-500 transition"
              onClick={handleModalClose}
            />
          </div>
        </div>

        {/* Workspace Body */}
        <div className="flex flex-1 flex-col md:flex-row overflow-hidden bg-white">
          
          {/* LEFT SIDE: Requirements Checklist */}
          <div className="w-full border-b border-gray-200 p-4 md:p-6 md:w-1/2 md:border-b-0 md:border-r overflow-y-auto">
            <label className="block mb-2 text-[0.75rem] font-bold text-gray-400 uppercase tracking-wider">
              {translate(202)}
            </label>
            <div className="space-y-3">
              {auctionData?.required_documents?.map((reqDoc) => {
                const approvedDoc = auctionData?.user_auction_documents?.find(
                  (d) => d.required_document?.id === reqDoc.id
                );
                const isSelected = selectedDocument?.id === reqDoc.id;

                const handleSelect = () => {
                  setSelectedDocument(reqDoc);
                  setError("");
                  setSelectedFile("");
                  if (approvedDoc) {
                    setShowPreview(approvedDoc.document);
                  } else {
                    setShowPreview("");
                  }
                };

                return (
                  <div
                    key={reqDoc.id}
                    onClick={handleSelect}
                    className={`flex items-center justify-between rounded-xl border p-4 cursor-pointer transition-all ${
                      isSelected ? "border-sky-500 bg-sky-50/40 shadow-xs" : "border-gray-100 bg-gray-50/50 hover:bg-gray-100/60"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                        approvedDoc ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"
                      }`}>
                        {approvedDoc ? <FiCheckCircle size={20} /> : <FiAlertCircle size={20} />}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">{reqDoc.name}</p>
                        <p className={`text-xs font-semibold ${approvedDoc ? "text-emerald-600" : "text-rose-500"}`}>
                          {approvedDoc ? "Verified" : "Missing"}
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!approvedDoc) {
                          setSelectedDocument(reqDoc);
                          setError("");
                          setShowPreview("");
                          setSelectedFile("");
                          triggerFileInput();
                        } else {
                          handleSelect();
                        }
                      }}
                      className={`flex items-center space-x-1 rounded-lg px-3 py-1.5 text-xs font-bold uppercase transition ${
                        approvedDoc 
                          ? "border border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                          : "bg-stone-800 text-white hover:bg-stone-900"
                      }`}
                    >
                      {approvedDoc ? (
                        <>
                          <FiEye className="mr-1" />
                          <span>{"Preview" || "VIEW"}</span>
                        </>
                      ) : (
                        <span>{translate(198) || "ATTACH"}</span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT SIDE: Canvas & Upload Area */}
          <div className="flex w-full flex-col justify-between p-4 md:p-6 md:w-1/2 bg-stone-50/40 overflow-y-auto">
            {selectedDocument ? (
              <div className="flex flex-col h-full justify-between space-y-4">
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-3">
                    Active Target: <span className="text-sky-700">{selectedDocument.name}</span>
                  </h4>

                  {error && <div className="mb-3 rounded-lg bg-red-50 p-2.5 text-xs text-red-600 font-medium border border-red-200">{error}</div>}

                  {/* Dropdown Selector */}
                  <div className="custom-dropdown mb-4 relative">
                    <button
                      type="button"
                      onClick={toggleDropdown}
                      className="flex justify-between w-full p-2 text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-gray-500"
                    >
                      <p className="truncate">{selectedDocument?.name || translate(203)}</p>
                      <p>▾</p>
                    </button>
                    {isOpen && (
                      <ul className="dropdown-list border-gray-200 border-2 bg-white rounded-xl mt-2 max-h-40 overflow-y-auto absolute z-10 w-full shadow-lg">
                        {missingDocuments?.map((title) => (
                          <li
                            key={title.id}
                            onClick={() => {
                              setSelectedDocument(title);
                              setIsOpen(false);
                              setShowPreview("");
                              setSelectedFile("");
                            }}
                            className="p-2 border-b cursor-pointer hover:bg-gray-50 text-sm text-gray-700"
                          >
                            {title.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Canvas Container */}
                  <div
                    className="rounded-lg overflow-hidden w-full cursor-pointer relative"
                    onMouseOver={() => setShowButton(true)}
                    onMouseOut={() => setShowButton(false)}
                    onClick={() => {
                      if (!auctionData?.user_auction_documents?.some(d => d.required_document?.id === selectedDocument.id)) {
                        triggerFileInput();
                      }
                    }}
                  >
                    <div className="border border-gray-300 flex justify-center items-center h-40 md:h-48 bg-gray-200 rounded-lg relative overflow-hidden">
                      
                      {/* Loading State Overlay */}
                      {documentLoading && (
                        <div className="absolute inset-0 bg-black/50 z-20 flex flex-col items-center justify-center text-white backdrop-blur-xs">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2" />
                          <span className="text-xs font-semibold">Uploading document...</span>
                        </div>
                      )}

                      {showPreview ? (
                        isPdf(showPreview) ? (
                          <div
                            className="w-full h-full bg-gray-100 flex flex-col items-center justify-center text-gray-600 gap-2 cursor-pointer p-4"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(showPreview, "_blank");
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2h6v2m2 4H7a2 2 0 01-2-2V7a2 2 0 012-2h5l2 2h5a2 2 0 012 2v10a2 2 0 01-2 2z" />
                            </svg>
                            <span className="text-xs font-semibold text-gray-700">PDF Document Selected (Click to Open)</span>
                          </div>
                        ) : (
                          <img src={showPreview} alt="Preview Target" className="w-full h-full object-contain bg-stone-950" />
                        )
                      ) : (
                        <p className="text-xs text-gray-500 px-4 text-center font-medium">
                          Please attach your document to preview
                        </p>
                      )}

                      {/* Attach Hover Overlay (Only if not uploaded) */}
                      {!auctionData?.user_auction_documents?.some(d => d.required_document?.id === selectedDocument.id) && (
                        <div
                          className={`absolute w-full bg-gray-800/70 text-white flex justify-center items-center cursor-pointer transition-all duration-200 h-10 bottom-0 ${
                            showButton ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
                          }`}
                        >
                          <span className="pr-2">
                            <IoCameraOutline className="text-lg" />
                          </span>
                          <span className="text-xs font-medium">{translate(198) || "ATTACH"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Upload Action Form */}
                <div className="mt-auto">
                  {!auctionData?.user_auction_documents?.some(d => d.required_document?.id === selectedDocument.id) && (
                    <>
                      <div className="flex items-center w-full mb-4">
                        <input
                          checked={agree}
                          onChange={(e) => setAgree(e.target.checked)}
                          id="agree3"
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 accent-lime-700 cursor-pointer"
                        />
                        <label htmlFor="agree3" className="ml-2 block text-xs text-gray-900 select-none cursor-pointer">
                          {translate(173)}
                          <Link to="/termsAndConditions" target="_blank" className="text-sky-600 underline font-medium ml-1">
                            {`(${translate(42)})`}
                          </Link>
                        </label>
                      </div>

                      <div className="flex justify-between items-center w-full space-x-4">
                        <button
                          type="button"
                          className="bg-stone-500 rounded w-1/2 py-2 text-sm text-white hover:bg-stone-700 transition"
                          onClick={handleModalClose}
                        >
                          {translate(63)}
                        </button>

                        <button
                          type="button"
                          className={`rounded w-1/2 py-2 text-sm font-semibold transition ${
                            !agree || !selectedFile || documentLoading
                              ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                              : "bg-lime-600 hover:bg-lime-700 text-white"
                          }`}
                          title={!agree ? translate(174) : translate(175)}
                          disabled={!agree || !selectedFile || documentLoading}
                          onClick={handleUploadSubmit}
                        >
                          {documentLoading ? "Uploading..." : translate(175)}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center text-center p-6 text-gray-400">
                <FiUploadCloud size={40} className="mb-2 stroke-1" />
                <p className="text-xs">Select a document requirement slot from the list to view or upload attachments.</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default DocReviewModal;